import { useState } from "react";
import { ArrowUp, ArrowDown, Trash2, GripVertical, Film, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDuration, thumbnailUrl, type MontageItem } from "@/lib/montageApi";

interface Props {
  montageId: string;
  item: MontageItem;
  index: number;
  count: number;
  disabled: boolean;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
  onTrim: (inPoint: number | null, outPoint: number | null) => Promise<void>;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  dragging: boolean;
}

export function MontageItemRow({
  montageId, item, index, count, disabled, onMove, onRemove, onTrim,
  onDragStart, onDragOver, onDrop, dragging,
}: Props) {
  const [inP, setInP] = useState(item.in_point ?? "");
  const [outP, setOutP] = useState(item.out_point ?? "");
  const [saving, setSaving] = useState(false);
  const full = item.in_point == null && item.out_point == null;
  const dirty = String(inP) !== String(item.in_point ?? "") || String(outP) !== String(item.out_point ?? "");

  const apply = async () => {
    setSaving(true);
    try {
      await onTrim(inP === "" ? null : Number(inP), outP === "" ? null : Number(outP));
    } finally {
      setSaving(false);
    }
  };

  const useFull = async () => {
    setInP("");
    setOutP("");
    setSaving(true);
    try {
      await onTrim(null, null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li
      data-testid={`montage-item-${item.id}`}
      draggable={!disabled}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        "flex flex-wrap items-center gap-4 rounded-2xl border border-[#2E7BFF]/20 bg-[linear-gradient(180deg,rgba(14,22,40,0.78),rgba(7,11,20,0.9))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-xl transition-[border-color,opacity,box-shadow]",
        dragging && "opacity-40",
        !disabled && "hover:border-[#2E7BFF]/60 hover:shadow-[0_0_30px_-14px_rgba(46,123,255,0.6)]",
      )}
    >
      <div className="flex items-center gap-3">
        <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" />
        <span
          data-testid={`montage-item-order-${item.id}`}
          className="grid h-8 w-8 place-items-center rounded-md border border-[#2E7BFF]/50 bg-[#2E7BFF]/15 font-display text-sm font-bold text-[#7FD4FF] shadow-[0_0_14px_-4px_rgba(46,123,255,0.8)]"
        >
          {index + 1}
        </span>
      </div>
      <div className="h-16 w-28 shrink-0 overflow-hidden rounded-md border border-[#2E7BFF]/30 bg-black shadow-[0_0_20px_-10px_rgba(46,123,255,0.6)]">
        {item.thumbnail_path ? (
          <img src={thumbnailUrl(montageId, item.id)} alt="" className="h-full w-full object-cover" />
        ) : (
          <Film className="m-auto mt-5 h-6 w-6 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p data-testid={`montage-item-name-${item.id}`} className="truncate text-sm font-semibold">
          {item.name}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {item.source_type === "clip" ? "Clip app" : "Upload"} · {item.width}×{item.height} · originale{" "}
          {fmtDuration(item.duration)} · segmento{" "}
          <span data-testid={`montage-item-effective-${item.id}`} className="text-[#3DDC84]">
            {fmtDuration(item.effective_duration)}
          </span>
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1 text-muted-foreground">
            IN
            <input
              data-testid={`montage-item-in-${item.id}`}
              type="number" min={0} step={0.1} max={item.duration} value={inP} disabled={disabled}
              onChange={(e) => setInP(e.target.value)} placeholder="0"
              className="field-premium h-8 w-24 !rounded-md px-2 text-xs"
            />
          </label>
          <label className="flex items-center gap-1 text-muted-foreground">
            OUT
            <input
              data-testid={`montage-item-out-${item.id}`}
              type="number" min={0} step={0.1} max={item.duration} value={outP} disabled={disabled}
              onChange={(e) => setOutP(e.target.value)} placeholder={item.duration.toFixed(1)}
              className="field-premium h-8 w-24 !rounded-md px-2 text-xs"
            />
          </label>
          <span className="text-muted-foreground">s</span>
          <button
            data-testid={`montage-item-trim-apply-${item.id}`}
            onClick={apply} disabled={disabled || saving || !dirty}
            className="inline-flex h-7 items-center gap-1 rounded border border-[#2E7BFF]/60 px-2 text-[#2E7BFF] disabled:opacity-40"
          >
            <Check className="h-3 w-3" /> Applica
          </button>
          <button
            data-testid={`montage-item-full-${item.id}`}
            onClick={useFull} disabled={disabled || saving || full}
            className="icon-action h-8 w-auto px-2.5 text-xs"
          >
            Video completo
          </button>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button data-testid={`montage-item-up-${item.id}`} onClick={() => onMove(-1)} disabled={disabled || index === 0}
          className="icon-action">
          <ArrowUp className="h-4 w-4" />
        </button>
        <button data-testid={`montage-item-down-${item.id}`} onClick={() => onMove(1)} disabled={disabled || index === count - 1}
          className="icon-action">
          <ArrowDown className="h-4 w-4" />
        </button>
        <button data-testid={`montage-item-remove-${item.id}`} onClick={onRemove} disabled={disabled}
          className="icon-action icon-action-danger">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}
